import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: "What experience do I need to start this program?",
    answer: "Even if you're completely new to IT, this program is suitable for you. We'll provide structured guidance to help you build the necessary skills, but we won't spoon-feed you. The goal is to equip you with real-world knowledge so you can think like a cloud engineer, not just follow instructions. If you're willing to put in the work, we'll help you get there!"

  },
  {
    question: "How long does it take to complete the program?",
    answer: "The course is self-paced, but realistically, it will take at least 6 months to complete. I'd be selling you a false dream if I claimed you could become a competent Cloud Engineer in less time. There's a lot to learn, Azure, automation, CI/CD, security, and real-world troubleshooting and you need time to practice, apply, and truly understand these skills. No shortcuts. If you're committed to putting in the work, CAP will guide you every step of the way."
  },
  {
    question: "Will I get hands-on experience with real cloud projects?",
    answer: "Yes! The program includes hands-on labs and real-world projects using Azure. You'll build a production-grade cloud infrastructure."
  },
  {
    question: "How does the certification guidance work?",
    answer: "We provide structured guidance for Azure, Terraform, and Kubernetes certifications. This includes study plans, practice questions, and exam tips."
  },
  {
    question: "What kind of support will I receive?",
    answer: <>
      Support varies by plan:
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><span className="font-medium">Basic:</span> Community forum access</li>
        <li><span className="font-medium">Pro:</span> 2 mentoring sessions per month, weekly group Q&A sessions and priority forum support</li>
        <li><span className="font-medium">Full Bootcamp:</span> 4 mentoring sessions per month, Direct messaging with instructors, 24/7 priority support and Weekly group Q&A sessions</li>
      </ul>
    </>
  },
  {
    question: "Can I switch plans later?",
    answer: "Yes, you can upgrade your plan at any time."
  },
  {
    question: "What's included in the bonus modules?",
    answer: "Bonus modules will cover other technical topics that are not covered in the main program but still important to know. This will be released as we go along."
  },
  {
    question: "What makes this program different from other cloud courses?",
    answer: "Unlike other bootcamps, we focus on Azure. We offer personalized mentoring from experienced cloud engineers, comprehensive certification guidance, and a clear career progression path. The curriculum is constantly updated with the latest Azure features and industry best practices."
  },
  {
    question: "Is there a money-back guarantee?",
    answer: "Yes, we offer a 14-day money-back guarantee for all plans. If you're not satisfied with the program, we'll provide a full refund within this period."
  }
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="bg-gradient-to-br from-[#F8FAFC] via-white to-[#F0F9FF] py-16">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#15213A] mb-4">Frequently Asked Questions</h2>
          <p className="text-gray-600">
            Know what you are getting
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow duration-200"
              initial={false}
            >
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-medium text-[#15213A] pr-4">{faq.question}</span>
                <svg
                  className={`w-5 h-5 text-[#1C98ED] transform transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-6 py-4 text-gray-600 border-t border-gray-100 bg-gray-50">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ; 